/** @module AuthAnalytics */
/* eslint-disable import/extensions */

import AuthSegmentAnalytics from './auth-segment-analytics.mjs';

/**
 * Aggregator class that manages multiple analytics providers
 * Delegates tracking calls to all configured providers in parallel
 * Static API matches the previous AuthSegmentAnalytics interface for backwards compatibility
 *
 * NOTE: Event/step/flow constants are in auth-analytics-constants.mjs
 * Import them separately: import { EVENT_NAME } from './auth-analytics-constants.mjs';
 */
class AuthAnalytics {
  static _providers = [];

  static get providers() {
    return this._providers;
  }

  /**
   * Initialize analytics providers from config array
   * @param {Array} providersConfig - Array of provider configs
   * @example
   * AuthAnalytics.initialize([
   *   { type: 'segment', writeKey: 'key1' },
   *   { type: 'segment', writeKey: 'key2' }  // Multiple Segment instances
   * ]);
   */
  static async initialize(providersConfig = []) {
    // Instantiate all providers based on their type
    this._providers = providersConfig.map((config) => {
      switch (config.type) {
        case 'segment':
          return new AuthSegmentAnalytics(config);
        // Future providers can be added here...
        default:
          console.warn(`Unknown analytics provider type: ${config.type}`);
          return null;
      }
    }).filter(p => p !== null);

    if (!this._providers || !this._providers.length) {
      console.warn('No analytics providers configured');
      return Promise.resolve();
    }

    // Initialize all providers in parallel
    const initPromises = this._providers.map(provider => provider.initialize(provider._options));
    await Promise.all(initPromises);
  }

  /**
   * Track event across all providers
   * @param {string} eventName - Event name
   * @param {object} properties - Event properties
   * @param {object} context - Event context
   * @returns {Promise<void>}
   */
  static async track(eventName, properties = {}, context = {}) {
    if (!this._providers || !this._providers.length) {
      console.warn('AuthAnalytics.track() called before initialization');
      return Promise.resolve();
    }

    // Track on all providers in parallel
    const trackPromises = this._providers.map(provider => provider.track(eventName, properties, context));
    await Promise.all(trackPromises);
  }

  /**
   * Build auth context object
   * Delegates to first provider (all use same logic from base class)
   */
  static buildAuthContext(options = {}) {
    if (!this._providers || !this._providers.length) {
      console.warn('AuthAnalytics.buildAuthContext() called before initialization');
      return {};
    }
    return this._providers[0].buildAuthContext(options);
  }

  /**
   * Build sign-in context object
   * Delegates to first provider (all use same logic from base class)
   */
  static buildSigninContext(options = {}) {
    if (!this._providers || !this._providers.length) {
      console.warn('AuthAnalytics.buildSigninContext() called before initialization');
      return {};
    }
    return this._providers[0].buildSigninContext(options);
  }

  /**
   * Build password reset context object
   * Delegates to first provider (all use same logic from base class)
   */
  static buildPasswordResetContext(options = {}) {
    if (!this._providers || !this._providers.length) {
      console.warn('AuthAnalytics.buildPasswordResetContext() called before initialization');
      return {};
    }
    return this._providers[0].buildPasswordResetContext(options);
  }

  /**
   * Register text entry tracking on blur events
   * Delegates to first provider (all use same DOM logic from base class)
   */
  static registerTextEntryTracking(element, callback, options = {}) {
    if (!this._providers || !this._providers.length) {
      console.warn('AuthAnalytics.registerTextEntryTracking() called before initialization');
      return;
    }
    return this._providers[0].registerTextEntryTracking(element, callback, options);
  }

  /**
   * Register checkbox tracking on change events
   * Delegates to first provider (all use same DOM logic from base class)
   */
  static registerCheckboxEvent(element, callback) {
    if (!this._providers || !this._providers.length) {
      console.warn('AuthAnalytics.registerCheckboxEvent() called before initialization');
      return;
    }
    return this._providers[0].registerCheckboxEvent(element, callback);
  }

  /**
   * Register button click tracking
   * Delegates to first provider (all use same DOM logic from base class)
   */
  static registerButtonClickEvent(element, callback) {
    if (!this._providers || !this._providers.length) {
      console.warn('AuthAnalytics.registerButtonClickEvent() called before initialization');
      return;
    }
    return this._providers[0].registerButtonClickEvent(element, callback);
  }
}

export default AuthAnalytics;
